import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { ApprovedSheet, Pairing } from "./pairing";

// The pairing decision, rendered the same way in both places it is made.
//
// It is ONE control with two homes: the table row, where it sits behind the
// baseline chip with the rest of the row's controls, and the drawing surface,
// where it sits in the decision bar under the two sheets. The question is
// identical — "what does this page supersede" — so the wording, the options and
// the caption come from here rather than from each screen. Two copies of this
// would drift, and what would drift is the thing the combined plan set is built
// on.
//
// NOTHING IS BLOCKED by anything here. The unmatched warning and the same-number
// question are both surfaced and neither stops a filing being generated: an
// operator made to clear a checklist before every filing stops reading it.

/** The select, plus the one line saying how the current answer was arrived at. */
export function PairingChoice({
  pairing,
  approvedSheets,
  disabled,
  onChoose,
}: {
  pairing: Pairing;
  approvedSheets: readonly ApprovedSheet[];
  disabled?: boolean;
  onChoose: (approvedSheetId: string | null) => void;
}) {
  const row = pairing.row;
  return (
    <div className="flex min-w-[15rem] flex-1 flex-col gap-1">
      <label className="flex flex-wrap items-center gap-2">
        <span className="label shrink-0 text-ink-muted">Supersedes</span>
        <select
          value={row.compareWith ?? ""}
          onChange={(e) => onChoose(e.target.value || null)}
          disabled={disabled}
          aria-label={`What ${row.sheetId || "this page"} supersedes`}
          className={cn(
            "min-w-0 flex-1 rounded-md border bg-surface px-2.5 py-1.5 text-[0.85rem] text-ink focus:border-primary focus:outline-none focus:shadow-ring disabled:opacity-50",
            pairing.unmatched ? "border-warn/60" : "border-rule",
          )}
        >
          <option value="">
            {pairing.basis === "sheetNumber"
              ? `Auto — ${pairing.approvedSheetId} by sheet number`
              : row.action === "N"
                ? "Nothing — this sheet is new"
                : "Auto — nothing matched"}
          </option>
          {approvedSheets.map((s, i) => (
            <option key={`${s.page}-${i}`} value={s.sheetId || `#${s.page}`}>
              {s.sheetId || "(unnamed)"} · p.{s.page} of approved
            </option>
          ))}
        </select>
      </label>
      <span className="text-[0.72rem] text-ink-muted">
        {pairing.basis === "operator"
          ? "you chose this"
          : pairing.basis === "sheetNumber"
            ? "matched by sheet number"
            : row.action === "N"
              ? "new sheet — nothing to supersede"
              : "nothing matched in the approved set"}
        {/* The note on this row was written against whatever the LAST
            comparison used. When that is no longer the pairing, the sentence is
            about a different pair and saying so is the only warning there is. */}
        {row.comparedTo &&
          pairing.approvedSheetId &&
          row.comparedTo.toUpperCase() !== pairing.approvedSheetId.toUpperCase() && (
            <span className="ml-1.5 text-ink">
              · the note was written against {row.comparedTo} — re-compare
            </span>
          )}
      </span>
    </div>
  );
}

/**
 * The same-number collision: this page is marked new, and a sheet of that
 * number is already approved.
 *
 * Deliberately NOT behind a disclosure and deliberately without a default. Left
 * alone the combined set carries both versions of the drawing — which is what
 * the app used to do silently, with a warning on a row buried at the end of a
 * ninety-page list.
 *
 * ONE LINE, not a panel, and that is not a style choice. On a real filing (1259
 * St Marks) thirteen of twenty-four sheets hit this at once, and thirteen
 * stacked warning panels turned the review table into something nobody could
 * read past — the question was so loud it stopped being a question. It reads as
 * a sentence with its answers at the end, and the set-wide version of it lives
 * above the table where thirteen of anything belongs.
 */
export function SameNumberPrompt({
  pairing,
  disabled,
  onSettle,
  onUnsettle,
  onDropRow,
}: {
  pairing: Pairing;
  disabled?: boolean;
  onSettle: (choice: "updated" | "both") => void;
  onUnsettle: () => void;
  /** "Keep the approved one" — this page is not part of the filing at all, so
   *  the row leaves the AI1. */
  onDropRow: () => void;
}) {
  const row = pairing.row;

  if (pairing.sameNumberChoice) {
    // Quiet once decided, but never invisible: she should be able to see on the
    // way past that this sheet had a collision and how it was settled.
    return (
      <div className="mt-1.5 flex w-full flex-wrap items-center gap-2 text-[0.78rem] text-ink-muted">
        <span>
          {pairing.sameNumberChoice === "updated"
            ? `Supersedes the approved ${row.sheetId}.`
            : `Filed alongside the approved ${row.sheetId} — both versions.`}
        </span>
        <button
          type="button"
          onClick={onUnsettle}
          disabled={disabled}
          className="underline decoration-dotted underline-offset-2 transition-colors hover:text-ink disabled:opacity-50"
        >
          change
        </button>
      </div>
    );
  }

  if (!pairing.newButPresent) return null;

  return (
    <div className="mt-1.5 flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-warn/50 bg-warn-soft px-2.5 py-1.5 text-[0.8rem] text-ink">
      <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-warn" />
      <span className="min-w-0">
        Marked new, but <span className="font-mono">{row.sheetId}</span> is
        already approved at p.{pairing.sameNumberPage}.
      </span>
      <PromptAction disabled={disabled} onClick={() => onSettle("updated")} primary>
        It supersedes it
      </PromptAction>
      <PromptAction disabled={disabled} onClick={onDropRow}>
        Keep the approved one
      </PromptAction>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSettle("both")}
        title="Rare, and legitimate — an architect can reuse a number for a different drawing. The filing would contain the drawing twice."
        className="rounded px-1 text-[0.76rem] text-ink-muted underline decoration-dotted underline-offset-2 transition-colors hover:text-ink disabled:opacity-50"
      >
        file both
      </button>
    </div>
  );
}

function PromptAction({
  children,
  primary,
  disabled,
  onClick,
}: {
  children: ReactNode;
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-md border px-2 py-0.5 text-[0.78rem] font-medium transition-colors disabled:opacity-50",
        primary
          ? "border-primary bg-primary text-white hover:bg-primary-ink"
          : "border-rule-strong bg-surface text-ink-soft hover:border-primary hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

/**
 * The same question asked once about the whole filing.
 *
 * Thirteen sheets reissued at 0.00 is ONE fact about a filing — the architect
 * did not bump the revisions — and asking it thirteen times is how a real
 * warning becomes wallpaper. Only the ordinary answer is offered in bulk: the
 * other two remove rows or file drawings twice, which is not something to do to
 * a whole set on one click. Every row still shows which way it went, with an
 * undo beside it.
 */
export function SameNumberBanner({
  count,
  disabled,
  onSettleAll,
}: {
  count: number;
  disabled?: boolean;
  onSettleAll: () => void;
}) {
  if (count < 2) return null;
  return (
    <div className="border-b border-rule bg-warn-soft px-6 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[0.85rem] text-ink">
        <span aria-hidden>⚠️</span>
        <span className="min-w-0">
          {count} sheets are marked new, but a sheet of the same number is
          already in the approved set — usually the revision was never bumped.
          Left as they are, the combined plan set would carry both versions.
        </span>
        <PromptAction disabled={disabled} onClick={onSettleAll} primary>
          They all supersede
        </PromptAction>
        <span className="text-[0.78rem] text-ink-muted">
          or answer them one at a time below
        </span>
      </div>
    </div>
  );
}
